<template>
  <div class="game-offer">
    <div class="game-offer__content container">
      <div
        v-if="offerScreenshots && offerScreenshots.length > 0"
        class="game-offer__slider-container">
        <ScreenshotsGallery
          :images="offerScreenshots"
          aspect-ratio="16 / 9" />

        <div
          v-if="topReview"
          class="game-offer__top-review">
          <transition
            name="fade"
            mode="out-in">
            <div
              :key="reviewIndex"
              class="game-offer__top-review-content">
              <ui-rate
                class="game-offer__top-review-stars"
                :value="Number(topReview.stars)"
                :show-rate-number="false"
                is-read-only />
              <p class="game-offer__top-review-comment">
                {{ topReview.review }}
              </p>
              <p class="game-offer__top-review-author">
                {{ topReview.author }}, {{ topReview.date }}
              </p>
            </div>
          </transition>
          <button
            class="game-offer__top-review-button button button-round-bordered"
            @click="scrollToReviews">
            More reviews
            <img
              src="/images/icons/arrow-simple.svg"
              alt="More reviews"
              width="16"
              height="16">
          </button>
        </div>
      </div>

      <AccountAdvantages />

      <AccountDetailsCard
        :details-info="mergedFields.singleValueFields"
        :description="offerDescription"
        class="game-offer__account-details" />

      <div class="game-offer__info">
        <AccountContentList
          v-for="(field, index) in mergedFields.multipleValuesFields"
          :key="index"
          :title="field.title"
          :content="field.values" />

        <FaqComponent
          v-if="faqs && faqs.length"
          :faqs="faqs" />
      </div>

      <div class="game-offer__order">
        <account-order-area
          v-if="accountOffer"
          ref="orderArea"
          class="game-offer__order-area"
          :current-price="currentPrice"
          :offer-data="accountOffer"
          :product="product" />
      </div>
    </div>

    <div
      v-if="showAdditionalOrderBtn"
      class="additional-buy-btn-wrapper">
      <button
        class="button button-main additional-buy-btn"
        @click="scrollToOrderArea">
        <img
          data-src="/images/icons/shield.svg"
          class="lazyload additional-buy-btn__img"
          width="14"
          height="14"
          alt="">
        Buy now for {{ currencyValue(displayPrice) }}
      </button>
    </div>

    <reviews-slider
      v-if="shuffledReviews && shuffledReviews.length"
      :key="activeGame.id"
      ref="orderReviews"
      title="SkyCoach reviews"
      slider-title="Gamers trust us"
      :reviews="shuffledReviews"
      :show-trust-box="false"
      :props-for-review-item="{ isAccountReview: true }"
      :breakpoints="sliderBreakpoints"
      :desktop-break-point="800"
      class="container game-service__reviews" />

    <base-card-list
      v-if="relatedProducts && relatedProducts.length"
      :scrollable-from="1023"
      :items="relatedProducts"
      class="game-offer__related container">
      <template #title>
        You may also like
      </template>

      <template #card="{ item }">
        <product-card
          :product="item"
          :show-card-footer="userDevice.innerWidth >= 1024" />
      </template>
    </base-card-list>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import { scrollIntoView } from 'Root/helpers/tools';
import { seededShuffle, assignReviewsDates } from 'Root/helpers/shuffleReviews';
import tinymceComponents from 'Root/mixins/tinymceComponents';
import ProductCard from 'Root/pages/game/components/ProductCard.vue';
import AccountDetailsCard from 'Root/pages/accounts/components/AccountDetailsCard.vue';
import FaqComponent from 'Root/pages/accounts/components/FaqComponent.vue';
import ScreenshotsGallery from 'Components/Accounts/ScreenshotsGallery.vue';
import AccountContentList from 'Components/Accounts/AccountContentList.vue';
import AccountOrderArea from 'Components/OrderArea/AccountOrderArea.vue';
import BaseCardList from 'Components/ui/BaseCardList/BaseCardList.vue';
import ReviewsSlider from 'Components/Reviews/ReviewsSlider.vue';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import AccountAdvantages from 'Components/Accounts/AccountAdvantages.vue';
import UiRate from 'Components/ui/Rating/UiRate.vue';

let buyButtonIntersectionObserver = null;

function initBuyButtonObserver() {
  const buyBtn = document.querySelector('.order-area__buy-now');
  if (buyBtn) {
    buyButtonIntersectionObserver.observe(buyBtn);
  }
}

export default {
  name: 'GameOfferAccount',

  components: {
    AccountAdvantages,
    ReviewsSlider,
    FaqComponent,
    AccountOrderArea,
    AccountContentList,
    ScreenshotsGallery,
    AccountDetailsCard,
    BaseCardList,
    ProductCard,
    UiRate,
  },

  mixins: [
    tinymceComponents,
  ],

  data() {
    return {
      showAdditionalOrderBtn: false,
      scrollPosition: 0,
      displayPrice: 0,
      sliderBreakpoints: {
        1380: {
          perView: 3,
        },
        1760: {
          perView: 4,
        },
        2560: {
          perView: 5,
        },
        3840: {
          perView: 6,
        },
      },
      isMounted: false,
      reviewIndex: 0,
      intervalId: null,
    };
  },

  computed: {
    ...mapState(['activeRegion', 'userDevice']),
    ...mapState(['activeGame', 'accountReviews']),
    ...mapState('products', ['product', 'accountOffer']),
    ...mapGetters(['currencyValue']),
    ...mapGetters('products', ['breadcrumbs']),
    ...mapGetters(['calcPriceFromUsd']),

    relatedProducts() {
      return (this.product?.related_products || [])
        .filter((p) => p.available_in_regions.includes(this.activeRegion.id));
    },

    currentPrice() {
      return this.calcPriceFromUsd(this.activeRegion.id, +this.accountOffer?.price_per_step, false, 4, true);
    },

    mergedFields() {
      if (!this.accountOffer || !this.product.product_currency_setting.product_currency_setting_fields) {
        return {
          multipleValuesFields: [],
          singleValueFields: [],
        };
      }

      const { data } = this.accountOffer;
      const fields = this.product.product_currency_setting.product_currency_setting_fields;

      return fields.reduce((result, field) => {
        if (Object.prototype.hasOwnProperty.call(data, field.id)) {
          const mergedField = {
            title: field.title,
          };

          // SKY-6039: support migration format of offer data with [null, null, null] values
          // hide fields with empty array values
          if (Array.isArray(data[field.id])
            && data[field.id].length
            && data[field.id].every((value) => value === null)) {
            mergedField.value = data[field.id].length;
            result.singleValueFields.push(mergedField);
          } else if (Array.isArray(data[field.id]) && data[field.id].length) {
            mergedField.values = data[field.id].map((valueId) => field
              .product_currency_setting_field_values.find((value) => value.id === valueId));

            // SKY-5783: сортируем значения в алфавитном порядке
            mergedField.values.sort((a, b) => a.value.localeCompare(b.value));

            result.multipleValuesFields.push(mergedField);
          } else if (!Array.isArray(data[field.id])) {
            mergedField.value = field.product_currency_setting_field_values.find(
              (value) => value.id === data[field.id],
            )?.value || data[field.id];
            result.singleValueFields.push(mergedField);
          }
        }

        return result;
      }, { multipleValuesFields: [], singleValueFields: [] });
    },

    offerScreenshots() {
      return this.accountOffer?.screenshots;
    },

    offerDescription() {
      return this.accountOffer?.description;
    },

    faqs() {
      return this.product?.product_currency_setting?.faq_content || [];
    },

    topReview() {
      return this.shuffledReviews[this.reviewIndex] || null;
    },

    shuffledReviews() {
      return assignReviewsDates(seededShuffle(this.accountReviews.slice(), new Date().getDate()));
    },
  },

  watch: {
    'product.id': {
      handler() {
        initBuyButtonObserver();
      },
    },
    'accountOffer.id': {
      handler() {
        this.trackEvent();
      },
    },
  },

  created() {
    if (this.product.product_currency_setting.id) {
      const isPreview = this.$route.query?.offer_preview === 'true';

      this.$store.dispatch('products/getAccountOfferAction', {
        currencySettingId: this.product.product_currency_setting.id,
        offerId: this.$route.params?.offerId,
        isPreview,
      });
    }
  },

  beforeDestroy() {
    clearInterval(this.intervalId);
    this.mutateAccountOffer(null);
  },

  mounted() {
    this.isMounted = true;

    buyButtonIntersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.showAdditionalOrderBtn = this.$store.state.userDevice.innerWidth < 1024
                && !entry.isIntersecting;
      });
    }, {
      threshold: 1,
      rootMargin: '32px 0px 0px 0px',
    });

    this.intervalId = setInterval(() => {
      this.reviewIndex = (this.reviewIndex + 1) % this.shuffledReviews.length;
    }, 7000);

    initBuyButtonObserver();

    this.trackEvent();
  },

  methods: {
    ...mapMutations('products', ['mutateAccountOffer']),

    scrollToOrderArea() {
      scrollIntoView(this.$refs.orderArea.$el);
    },

    scrollToReviews() {
      scrollIntoView(this.$refs.orderReviews.$el);
    },

    trackEvent() {
      if (!this.accountOffer) {
        return;
      }

      try {
        const productTag = { title: 'Accounts' };
        const product = {
          ...this.product,
          title: this.accountOffer.outer_name,
        };
        googleTagAnalytics.firePageView(product, productTag, this.currentPrice);
      } catch (e) {
        console.error(e);
      }
    },
  },
};
</script>

<style lang="scss">
.additional-buy-btn-wrapper {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100vw;
  padding: 20px;
  background-color: rgba($color--additional-gray, .95);
  z-index: 1000;
}

.additional-buy-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 10px;
  width: 100%;
}

.additional-buy-btn__img {
  margin-right: 9px;
  height: auto;
}
</style>

<style lang="scss" scoped>
.game-offer .game-offer__related {
  --item-width: 250px;

  /deep/ .card-list__title {
    font-size: $text-25px;
  }
}

.game-offer__content {
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: repeat(auto-fill, minmax(100px, 1fr));
  grid-gap: 24px;
  padding-top: 8px;
}

.game-offer__info {
  padding: 0 var(--padding);
  display: grid;
  grid-template-columns: 1fr;
  min-width: 0;
}

.game-offer__top-review {
  position: absolute;
  top: 93px;
  right: 42px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 260px;
  padding: 24px;
  background-color: $color--basic-menu;
  border-radius: 16px;
  transition: height 0.2s;
}

.game-offer__top-review-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.game-offer__top-review-stars {
  --star-size: 16px;

  /deep/.ui-rate__stars {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  /deep/.ui-rate__star {
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
}

.game-offer__top-review-comment {
  font-weight: 400;
  font-size: $text-13px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -moz-box;
  -moz-box-orient: vertical;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  line-clamp: 6;
  box-orient: vertical;
}

.game-offer__top-review-author {
  font-weight: 400;
  font-size: $text-13px;
  line-height: normal;
  color: $color--basic-dark-gray;
}

.game-offer__top-review-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: auto;

  img {
    transform: rotate(90deg);
  }
}

.game-offer__order-area {
  align-self: flex-start;
  position: relative;
  z-index: 4;
}

.game-offer__related {
  padding-top: 40px;
  background-color: $color--basic-black;
}

.game-offer__platforms {
  font-size: 13px;
  font-weight: 500;
  color: $color--basic-dark-gray;
  margin-bottom: 14px;
  position: relative;
  z-index: 2;
}

.game-offer__breadcrumbs {
  margin-bottom: 5px;
}

.game-offer__account-reviews {
  display: grid;
  grid-template-columns: 200px minmax(300px, 427px);
  margin: auto;
}

.game-offer__account-reviews-text {
  font-size: 16px;
  font-weight: 500;
  margin-left: auto;
  margin-right: 12px;
  align-self: center;
}

.game-offer__slider-container {
  position: relative;
  min-width: 0;

  /deep/ .image-gallery {
    padding: 24px 24px 32px 24px;
  }
}

@media screen and #{$screen-more-or-768} {
  .game-offer__order-area {
    position: sticky;
    top: 20px;
  }
}

@media screen and #{$screen-less-than-1280} {
  .game-offer__content {
    grid-template-columns: 1fr 1fr;
  }

  .game-offer__account-details {
    grid-column: 1 / -1;
  }

  .game-offer__info {
    padding: 0;
    grid-column: span 2;
    grid-row-gap: 24px;
  }

  .game-offer__slider-container {
    grid-column: 1 / -1;
  }

  .game-offer__order {
    grid-column: 2;
    grid-row: 2;
  }

  .game-offer__order {
    position: relative;
  }

  .game-offer__account-reviews {
    grid-column: span 2;
  }
}

@media screen and #{$screen-more-or-1280} {
  .game-offer__info {
    padding: 0;
    min-height: 450px;
    display: grid;
    grid-auto-flow: row;
    grid-row-gap: 24px;
    align-items: flex-start;
  }

  .game-offer__content {
    grid-gap: 24px;
    grid-template-columns: 1fr 310px;
  }

  .game-offer__order {
    position: sticky;
    top: 20px;
    grid-column: 2;
    grid-row: 1 / span 4;
  }

  .game-offer__platforms {
    text-align: right;
  }
}

@media screen and #{$screen-less-than-640} {
  .container {
    padding: 12px 16px;
  }

  .game-offer__content {
    grid-template-columns: 1fr;
    grid-gap: 0;
    grid-row-gap: 24px;
    overflow: hidden;
  }

  .game-offer__order {
    grid-column: 1;
    grid-row: 4;
  }

  .game-offer__info {
    grid-column: 1 / -1;
  }

  .game-offer__slider-container {
    display: contents;
  }

  .game-offer__top-review {
    position: static;
    top: 0;
    right: 0;
    width: 100%;
  }

  .game-offer__account-reviews {
    grid-template-columns: 1fr;
    grid-column: 1;
    grid-row-gap: 12px;
  }

  .game-offer__account-reviews-text {
    align-self: auto;
    text-align: center;
    margin-left: 0;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s ease;
}

.fade-enter {
  opacity: 0;
  transform: translateX(-24px);
}

.fade-enter-to {
  opacity: 1;
  transform: translateX(0);
}

.fade-leave {
  opacity: 1;
  transform: translateX(0);
}

.fade-leave-to {
  opacity: 0;
  transform: translateX(24px);
}
</style>
